'use client';

import type { FC, ReactNode } from 'react';
import { cn } from '../../../utils/cn';
import { Credits } from '../../Credits';
import { FaArrowUp } from '../../icons';
import { useHaptic } from '../../../hooks/useHaptic';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import type { CatalogSort } from './catalog.types';
import translations from './CaseCatalog.translations';

interface CatalogSortBarProps {
  value: CatalogSort;
  onChange: (sort: CatalogSort) => void;
  className?: string;
}

const Item: FC<{ active: boolean; onClick: () => void; className?: string; 'aria-label'?: string; children: ReactNode }> = ({
  active,
  onClick,
  className,
  children,
  ...rest
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'inline-flex items-center gap-1.5 h-11 text-[15px] font-medium transition-colors [-webkit-tap-highlight-color:transparent] active:opacity-70',
      active ? 'text-label-primary' : 'text-label-secondary',
      className,
    )}
    {...rest}
  >
    {children}
  </button>
);

const Dot: FC = () => <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-accent-blue" />;

export const CatalogSortBar: FC<CatalogSortBarProps> = ({ value, onChange, className }) => {
  const { t } = useLocalTranslation(translations);
  const { impact } = useHaptic();
  const byPrice = value === 'price-asc' || value === 'price-desc';

  const pick = (sort: CatalogSort) => {
    if (sort === value) return;
    impact('soft');
    onChange(sort);
  };

  return (
    <div className={cn('flex items-center gap-[22px]', className)}>
      <Item active={value === 'new'} onClick={() => pick('new')}>
        {t('sortNew')}
        {value === 'new' && <Dot />}
      </Item>
      <Item active={value === 'top'} onClick={() => pick('top')}>
        {t('sortTop')}
        {value === 'top' && <Dot />}
      </Item>
      <Item
        active={byPrice}
        onClick={() => pick(value === 'price-asc' ? 'price-desc' : 'price-asc')}
        aria-label={t('sortPrice')}
        className="ms-auto gap-0.5"
      >
        <Credits amount={0} iconOnly iconSize={16} />
        <FaArrowUp size={14} className={cn('transition-transform', value === 'price-desc' && 'rotate-180')} />
      </Item>
    </div>
  );
};
